import React, { useState, useEffect, useRef } from "react";
import {
    StyleSheet,
    View,
    SafeAreaView,
    useWindowDimensions,
    ActivityIndicator,
    Pressable,
    ScrollView,
    FlatList,
} from "react-native";
import Modal from "react-native-modal";
import CustomLabel from "../../../components/CustomLabel/CustomLabel";
import CustomInput from "../../../components/CustomInput";
import CustomButton from "../../../components/CustomButton";
import CustomDropDown from "../../../components/CustomDropDown/CustomDropdown";
import NewLeaveScreen from "../../SelfService/MyLeaves/NewLeaveScreen";
import { useTranslation } from "react-i18next";
import { useTheme } from "@react-navigation/native";
import { useAccountContext } from "../../../hooks/useAccountContext";
import { useAuthContext } from "../../../hooks/useAuthContext";
import { useSettingsContext } from "../../../hooks/useSettingsContext";
import { createCommonStyles } from "../../../styles/commonStyles";
import { useLeave } from "../../../hooks/useLeave";
import EmployeeList from "../../../components/EmployeeList/EmployeeList";

const AdminLeaveManagementScreen = () => {

    const { colors } = useTheme();
    const { t } = useTranslation();
    const { width, height } = useWindowDimensions();
    const styles = makeStyles(colors, width, height);
    const common = createCommonStyles(colors);
    const { GetAdminLeave, GetAdminSelectedEmlpoyeeLeave, leaveApproveRejUnsub,
        GetFiscalYear, GetLeaveStatusCount, DeleteLeave, isLoading } = useLeave();
    const { addApproverComments } = useSettingsContext();
    const { username } = useAuthContext();
    const { account } = useAccountContext();
    const [employees, setEmployees] = useState([]);
    const [leaveRecords, setLeaveRecords] = useState([]);
    const [selectedEmployee, setSelectedEmployees] = useState({});
    const [selectedRecord, setSelectedRecord] = useState({});
    const [searchQuery, setSearchQuery] = useState(undefined);
    const [statusTotals, setStatusTotals] = useState(undefined);
    const [selectedStatus, setSelectedStatus] = useState(t('common:allLeaves'));
    const [showNewLeaveScreen, setShowNewLeaveScreen] = useState(false);
    const [laaEmplId, setLaaEmplId] = useState("");
    const [laaCompany, setLaaCompany] = useState("");
    const [leaveDetails, setLeaveDetails] = useState({});
    const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());
    const filterYearRef = useRef(null);
    const [years, setYears] = useState([]);

    //#region UseEffect
    //* Async Employee Loader.
    const fetchAdminLeave = async (searchFilter, year, selectedStatus) => {
        await GetAdminLeave(searchFilter, year, selectedStatus)
            .then((resp) => setEmployees(resp))
            .catch((err) => console.log(err));
    };

    const fetchLeaveStatusCount = async () => {
        await GetLeaveStatusCount(filterYear)
            .then((resp) => {
                setStatusTotals(resp)
            })
            .catch((err) => console.log(err));
    }

    const fetchSelectedEmplLeave = async () => {
        await GetAdminSelectedEmlpoyeeLeave(selectedEmployee?.employeeId, '')
            .then((resp) => {
                setLeaveRecords(resp)
                setShowNewLeaveScreen(false);
            })
            .catch((err) => console.log(err));
    };

    useEffect(() => {
        //* Get Current Fiscal year and fill years with after 2 years n
        GetFiscalYear().then((resp) => {
            fetchLeaveStatusCount(resp).catch();
            const currentYear = resp;
            filterYearRef.current = resp;
            const years = [];
            years.push({ label: "All", value: "all" });
            for (let i = 2028; i >= 2016; i--) {
                years.push({ label: i.toString(), value: i.toString() });
            }
            setYears(years);
        }).catch();

    }, []);

    useEffect(() => {
        if (Object.keys(selectedEmployee).length === 0) return;
        fetchSelectedEmplLeave().catch();
    }, [selectedEmployee])

    useEffect(() => {
        let status = '';
        switch (selectedStatus) {
            case t('common:submitted'):
                status = 'Pending';
            case t('common:approved'):
                status = 'Approved';
            case t('common:rejected'):
                status = 'Rejected';
            case t('common:unsub'):
                status = 'Unsubmitted';
        }
        fetchAdminLeave('', filterYear, selectedStatus).catch();
    }, [selectedStatus])


    // * Delay to avoid spaming API on every letter typed 
    useEffect(() => {
        if (searchQuery !== undefined) {
            const delayDebounceFn = setTimeout(() => {
                fetchAdminLeave(searchQuery, filterYear, selectedStatus).catch();
            }, 500);
            return () => clearTimeout(delayDebounceFn);
        } else {
            return;
        };
    }, [searchQuery]);

    //* Reload employees on change year 
    useEffect(() => {
        if (filterYear != filterYearRef.current && filterYearRef.current !== null) {
            filterYearRef.current = filterYear;
            fetchAdminLeave('', filterYear).then(() => {
                fetchLeaveStatusCount(filterYear).catch();
            }).catch();
        }
    }, [filterYear])

    //#endregion

    //#region Helpers
    const sectionDataTransform = employees?.reduce((acc, item) => {
        const firstChar = item.name.trim()[0].toUpperCase();
        const section = acc.find(s => s.title === firstChar);
        if (section) section.data.push(item);
        else acc.push({ title: firstChar, data: [item] });
        return acc;
    }, []);

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        const d = new Date(dateStr);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const statusFilters = [
        { id: 0, primary: t('common:allLeaves'), secondary: statusTotals?.total, tertiary: t('common:allLeavesMinor') },
        { id: 1, primary: t('common:submitted'), secondary: statusTotals?.submitted, tertiary: t('common:submittedMinor') },
        { id: 2, primary: t('common:approved'), secondary: statusTotals?.approved, tertiary: t('common:approvedMinor') },
        { id: 3, primary: t('common:rejected'), secondary: statusTotals?.rejected, tertiary: t('common:rejectedMinor') },
        { id: 4, primary: t('common:unsub'), secondary: statusTotals?.unsubmitted, tertiary: t('common:unsubMinor') },
    ];
    //#endregion

    //#region Renders 
    const StatefulModalContent = ({ item, setIsModalOpen, typeOfApproval }) => {
        const [comments, setComments] = useState();
        const isReject = typeOfApproval === 'reject';
        return (
            <View style={styles.modalContent}>
                <CustomInput
                    type='LAA_Comments'
                    multiline={true}
                    numberOfLines={3}
                    placeholder='Comments (Optional)'
                    value={comments}
                    setValue={setComments}
                />
                <View
                    style={styles.rowModal}>
                    <CustomButton
                        text={isReject ? t('common:reject') : t('common:approve')}
                        onPress={() => {
                            leaveApproveRejUnsub({ LeaveId: item.rowGuid, Username: username, Comments: comments }, isReject ? 'Reject' : 'Approve', true)
                                .then((resp) =>
                                    fetchSelectedEmplLeave()
                                ).catch(console.error);
                        }}
                        type={isReject ? "REJECT" : "APPROVE"}
                        iconName="close-outline"
                        sz={20}
                        show="HIDE"
                        isGradient={isReject ? false : true}
                    />
                    <View style={common.verticalDivider}></View>
                    <CustomButton
                        onPress={() => setIsModalOpen(false)}
                        type={isReject ? "REJECT" : "APPROVE"}
                        fgColor="#1C5103"
                        text={t('common:cancel')}
                        iconName="checkmark-outline"
                        sz={20}
                        show="HIDE"
                    />
                </View>
            </View>
        );
    };

    const RenderData = (record) => {
        const [isModalOpen, setIsModalOpen] = useState(false);
        const [typeOfApproval, setTypeOfApproval] = useState('');

        let status = '';
        let specificColor = '';
        let textColor = '';
        if (record.item.status == 'Approved') {
            status = 'APPROVED'
            specificColor = colors.background_approved
            textColor = colors.text_approved
        }
        else if (record.item.status == 'Rejected') {
            status = 'REJECTED'
            specificColor = colors.background_rejected
            textColor = colors.text_pending
        }
        else if (record.item.status == 'Unsubmitted') {
            status = 'UNSUBMITTED'
            specificColor = colors.card_pending
            textColor = colors.card_pending
        }
        else {
            status = 'PENDING'
            specificColor = colors.card_pending
            textColor = colors.text_pending
        }

        return (
            <View>
                <Pressable
                    onPress={() => setSelectedRecord(record.item)}
                // style={() => [record.item.rowguid === selectedRecord?.record ? styles.selectedRecord : null]}
                >
                    <View style={[common.cardstyle, styles.recordRow]}>
                        <CustomLabel type='AVAILABLEUSED' value={record.item.absenceType} />
                        <CustomLabel type='AVAILABLEUSED_SECONDARY' value={formatDate(record.item.startDate)} />
                        <CustomLabel type='AVAILABLEUSED_SECONDARY' value={formatDate(record.item.endDate)} />
                        <CustomLabel type='AVAILABLEUSED_SECONDARY' value={record.item.duration} />
                        <CustomLabel value={status} type='STATUS' specificColor={specificColor} textColor={textColor} />

                        {/* rejected show button if leave is not unsubmitted and not rejected  */}
                        {(status !== 'REJECTED' && status !== 'UNSUBMITTED') && < CustomButton
                            onPress={() => {
                                setIsModalOpen(true);
                                setTypeOfApproval('reject');
                            }}
                            type="REJECTLAA"
                            show='REJECTLAA'
                            iconName="close-outline"
                            sz={25}
                        />}

                        {(status !== 'APPROVED' && status !== 'UNSUBMITTED') && <CustomButton
                            onPress={() => {
                                if (addApproverComments) {
                                    setIsModalOpen(true);
                                    setTypeOfApproval('approve');
                                } else {
                                    leaveApproveRejUnsub({ LeaveId: record.item.rowGuid, Username: username, Comments: '' }, 'Approve', true)
                                        .then((resp) =>
                                            fetchSelectedEmplLeave()
                                        ).catch(console.error);
                                }
                            }}
                            iconName="checkmark-outline"
                            sz={25}
                            type="APPROVELAA"
                            show='APPROVELAA'
                            isGradient={true}
                        />
                        }

                        {(status !== 'UNSUBMITTED') &&
                            <CustomButton
                                onPress={() => {
                                    leaveApproveRejUnsub({ LeaveId: record.item.rowGuid, Username: username, Comments: '' }, 'Unsubmit', true)
                                        .then((resp) =>
                                            fetchSelectedEmplLeave()
                                        ).catch(console.error);
                                }}
                                type="REJECTLAA"
                                show='REJECTLAA'
                                iconName="arrow-undo-outline"
                                sz={25}
                            />
                        }
                        {/* Edit leave */}
                        {(status === 'UNSUBMITTED') && <CustomButton
                            onPress={() => {
                                setLeaveDetails({
                                    editLeave: true,
                                    acode: record.item.aCode,
                                    startDate: record.item.startDate.split('T')[0],
                                    endDate: record.item.endDate.split('T')[0],
                                    comments: record.item.comments,
                                    leaveId: record.item.rowGuid,
                                    replacementId: record.item.replacementid
                                })
                                setLaaEmplId(record.item.employeeId);
                                setLaaCompany(record.item.company)
                                setShowNewLeaveScreen(true);
                            }}
                            type="APPROVELAA"
                            show='APPROVELAA'
                            isGradient={true}
                            iconName="pencil-outline"
                            sz={25}
                        />
                        }

                        {(status === 'UNSUBMITTED') && <CustomButton
                            onPress={() => DeleteLeave(record.item.rowGuid).then(() => { fetchSelectedEmplLeave().catch() }).catch()}
                            iconName="trash-outline"
                            show="HOME_BELL"
                            type="HOME_BELL"
                            textTop="0"
                            sz={30}
                        />
                        }
                    </View>
                </Pressable>
                <Modal
                    isVisible={isModalOpen}
                    backdropColor={colors.background_approved}
                    backdropOpacity={0.9}
                    onBackdropPress={() => {
                        setIsModalOpen(false);
                    }}
                    onRequestClose={() => {
                        setIsModalOpen(false);
                    }}>
                    <SafeAreaView
                        style={styles.container}>
                        <StatefulModalContent
                            item={record.item}
                            typeOfApproval={typeOfApproval}
                            setIsModalOpen={setIsModalOpen} />

                    </SafeAreaView>
                </Modal>
            </View>
        )
    };
    //#endregion

    if (isLoading) {
        return (
            <SafeAreaView style={styles.main}>
                <ActivityIndicator animating size='large' color="#86BC25" />
            </SafeAreaView>
        );
    }

    return (

        <SafeAreaView style={styles.main}>
            {/* Summary stats row */}
            <View style={styles.summary}>
                <View style={common.titleButtonRow}>
                    <CustomLabel
                        value={t("navigate:LAAadmin")}
                        type="MINORTITLE"
                    />
                    <CustomDropDown
                        defValue={filterYear}
                        options={years}
                        value={filterYear}
                        setValue={setFilterYear}
                        type='TIMESHEET'
                    />
                </View>
                <View style={common.divider} />
                <View style={styles.row}>
                    {statusFilters.map(item => (
                        <Pressable
                            onPress={() => setSelectedStatus(item.primary)}
                            key={item.id}
                            style={[
                                common.cardstyle,
                                item?.primary === selectedStatus ? styles.rowItemsSelected : styles.rowItems
                            ]}>
                            <CustomLabel type='AVAILABLEUSED' value={item.primary} fontSize={18} />
                            <CustomLabel type='AVAILABLEUSED_MUTED' value={item.secondary} fontSize={16} />
                            <CustomLabel type='AVAILABLEUSED_SECONDARY' value={item.tertiary} fontSize={16} />
                        </Pressable>
                    ))}
                </View>
            </View>

            {/* Analytics — employee list + leave records + detail */}
            <View style={styles.analytics}>
                <View style={styles.row}>
                    {/* Employee list */}
                    <View style={styles.empColumn}>
                        <CustomLabel type='MINORTITLE' value={t('navigate:hrAdminEmployees')} />
                        <EmployeeList
                            employees={sectionDataTransform}
                            isLoading={isLoading}
                            selectedEmployee={selectedEmployee}
                            setSelectedEmployee={setSelectedEmployees}
                            searchQuery={searchQuery}
                            setSearchQuery={setSearchQuery}
                        />
                    </View>
                    {showNewLeaveScreen ?
                        <View style={styles.newLeave}>
                            <ScrollView>
                                <NewLeaveScreen
                                    leaveDetails={leaveDetails}
                                    managerId={account.employeeId}
                                    laaEmplId={laaEmplId}
                                    laaCompany={laaCompany}
                                    setShowNewLeaveScreen={setShowNewLeaveScreen}
                                    fetchSelectedEmplLeave={fetchSelectedEmplLeave}
                                /></ScrollView>
                        </View> : <View style={styles.rowRenderDetails}>
                            {/* Leave records */}
                            <View style={styles.recordColumn}>
                                <View style={styles.rowRecord}>
                                    <CustomLabel type='MINORTITLE' value={t('common:leaveRecords')} />
                                    {(Object.keys(selectedEmployee).length) > 0 && <CustomButton
                                        onPress={() => {
                                            setLeaveDetails(null)
                                            setLaaEmplId(selectedEmployee?.employeeId);
                                            setLaaCompany(selectedEmployee?.company)
                                            setShowNewLeaveScreen(true);
                                        }
                                        }
                                        iconName="add-outline"
                                        show="HOME_BELL"
                                        type="HOME_BELL"
                                        textTop="0"
                                        sz={30}
                                    />}
                                </View>
                                <View style={common.divider} />
                                <FlatList
                                    data={leaveRecords}
                                    renderItem={
                                        (item) => (<RenderData {...item} />)
                                    }
                                    keyExtractor={(item, index) => String(index)}
                                    removeClippedSubviews={false}
                                    scrollEnabled={true}
                                    refreshing={isLoading}
                                    initialNumToRender={4}
                                    ListEmptyComponent={
                                        <View style={[common.cardstyle]}>
                                            <CustomLabel type='AVAILABLEUSED' value={t('common:noEmployeeSelected')} />
                                            <CustomLabel type='AVAILABLEUSED_MUTED' value={t('common:plsSelectLeave')} />
                                        </View>
                                    }
                                    ItemSeparatorComponent={() => <View style={{ height: 8 }} />}
                                    contentContainerStyle={{ paddingVertical: 8 }}
                                />
                            </View>

                            {/* Detail panel */}
                            <View style={[styles.detailsColumn]}>
                                <CustomLabel type='MINORTITLE' value={t('common:LRDs')} />
                                <View style={common.divider} />

                                {Object.keys(selectedRecord).length === 0 ?
                                    <View style={[common.cardstyle]}>
                                        <CustomLabel type='AVAILABLEUSED' value={t('common:noLeaveSelected')} />
                                        <CustomLabel type='AVAILABLEUSED_MUTED' value={t('common:plsSelectLeave')} />
                                    </View> :
                                    <View style={[common.cardstyle]}>
                                        <View style={styles.recordRow}>
                                            <CustomLabel type='AVAILABLEUSED' value={t('common:LT')} />
                                            <CustomLabel type='AVAILABLEUSED_SECONDARY' value={selectedRecord?.absenceType} />
                                        </View>
                                        <View style={common.divider} />
                                        <View style={styles.recordRow}>
                                            <CustomLabel type='AVAILABLEUSED' value={t('common:startDate')} />
                                            <CustomLabel type='AVAILABLEUSED_SECONDARY' value={formatDate(selectedRecord?.startDate)} />
                                        </View>
                                        <View style={common.divider} />
                                        <View style={styles.recordRow}>
                                            <CustomLabel type='AVAILABLEUSED' value={t('common:endDate')} />
                                            <CustomLabel type='AVAILABLEUSED_SECONDARY' value={formatDate(selectedRecord?.endDate)} />
                                        </View>
                                        <View style={common.divider} />
                                        <View style={styles.recordRow}>
                                            <CustomLabel type='AVAILABLEUSED' value={t('common:Dur')} />
                                            <CustomLabel type='AVAILABLEUSED_SECONDARY' value={selectedRecord?.duration} />
                                        </View>
                                        <View style={common.divider} />
                                        <View style={styles.recordRow}>
                                            <CustomLabel type='AVAILABLEUSED' value={t('common:status')} />
                                            <CustomLabel type='AVAILABLEUSED_SECONDARY' value={selectedRecord?.status} />
                                        </View>
                                        <View style={common.divider} />
                                        <View style={styles.recordRow}>
                                            <CustomLabel type='AVAILABLEUSED' value={t('common:Comments')} />
                                            <CustomLabel type='AVAILABLEUSED_SECONDARY' value={selectedRecord?.comments} />
                                        </View>
                                        <View style={common.divider} />
                                        <View style={styles.recordRow}>
                                            <CustomLabel type='AVAILABLEUSED' value={t('common:Year')} />
                                            <CustomLabel type='AVAILABLEUSED_SECONDARY' value={selectedRecord?.year} />
                                        </View>
                                    </View>
                                }
                            </View>
                        </View>
                    }
                </View>
            </View>

        </SafeAreaView>
    );
};

const makeStyles = (colors, width, height) =>
    StyleSheet.create({
        main: {
            flex: 1,
        },
        summary: {
            flex: 1,
        },
        analytics: {
            flex: 3,
        },
        newLeave: {
            flex: 3,
        },
        row: {
            flex: 1,
            flexDirection: 'row',
        },
        rowModal: {
            flex: 1,
            alignSelf: 'center',
            justifyContent: 'space-between',
            flexDirection: 'row',
        },
        rowRenderDetails: {
            flex: 3,
            flexDirection: 'row',
        },
        empColumn: {
            flex: 1,
            marginHorizontal: 5,
        },
        recordColumn: {
            flex: 2,
            marginHorizontal: 5,
        },
        detailsColumn: {
            flex: 1,
            marginHorizontal: 5,
        },
        rowItems: {
            flex: 1,
            margin: 8,
        },
        rowRecord: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',      // optional, for vertical alignment
            marginHorizontal: 20,
        },
        rowItemsSelected: {
            flex: 1,
            margin: 8,
            borderRadius: 10,
            borderWidth: 3,
            borderColor: colors.green
        },
        recordRow: {
            flexDirection: 'row',
            alignItems: 'center',
            paddingVertical: 12,
            paddingHorizontal: 14,
            zIndex: 10,
        },
        selectedRecord: {
            borderWidth: 4,
            borderColor: colors.buttons_primary,
            elevation: 5,
            shadowColor: colors.card_line,
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.3,
            shadowRadius: 5,
        },
    });

export default AdminLeaveManagementScreen;